import type { TraceEntryResource } from '@repo/api-client';

import { formatPaise } from '@/lib/money';

import {
  formatMovementQuantity,
  labelForReason,
  type MovementReason,
} from '../stock/movement-reasons';
import { labelForReferenceType } from './cash-book-entries';

/**
 * How one row of a timeline is read, and how a business day's settlement is.
 *
 * The trace is the only read in this epic that returns rows from **six different
 * tables in one list**, and `source` is what says how to read the nullable
 * columns beside it: a stock movement has a signed `quantity` and no money, a
 * ledger entry has money and a direction and no quantity, an audit entry has
 * neither and no outlet at all. Nothing here infers a source from which columns
 * happen to be filled — `source` is published, and reading it is cheaper and
 * cannot be wrong.
 *
 * ## The null this module exists for
 *
 * `TraceDayResource.state` is `'CLOSED' | 'OPEN' | null`, and **on this route the
 * null is genuine**. A date can carry activity that no reachable outlet opened a
 * business day on — reachable today through a receipt reprint, which is dated
 * from its own recorded instant because `audit_entries` carries no business day.
 * A date nobody opened is stateless rather than `OPEN`: nothing is in progress.
 *
 * So `describeDayState` answers `null` and the screen renders the date and its
 * figures with **no badge at all**. Not `Open`, not `Unknown`, and not a dash —
 * a dash in a column of states reads as a zero, and "this day is settled" is
 * exactly the fact a reader would then get wrong in the safe-looking direction.
 *
 * This is the opposite call from the item reads, where a same-looking null is a
 * schema defect (`blossom-rest-api#465`). The difference is in the document: the
 * trace's own field says "never null on the item reads" in as many words, which
 * is the API telling us which of the two it is.
 *
 * ## The sign is never moved onto the amount
 *
 * A ledger `amountPaise` is a non-negative magnitude and `direction` carries the
 * sign, "so a sum cannot net two directions together by arithmetic alone" — the
 * same rule the cash book is built on, and `describeDirection` there is where the
 * words live. A stock movement is the other way round: the sign **is** the
 * movement, there is no separate direction, and it is rendered.
 */

/** Every table the timeline draws from, as the route names them. */
export const TRACE_SOURCES = [
  'sales',
  'sale_lines',
  'sale_refunds',
  'stock_movements',
  'ledger_entries',
  'audit_entries',
] as const satisfies readonly TraceEntryResource['source'][];

export type TraceSource = (typeof TRACE_SOURCES)[number];

const SOURCE_LABELS: Readonly<Record<TraceSource, string>> = {
  sales: 'Sale',
  sale_lines: 'Basket line',
  sale_refunds: 'Refund',
  stock_movements: 'Stock movement',
  ledger_entries: 'Ledger entry',
  audit_entries: 'Audit',
};

/** What kind of record a row is, named as a person would name it. */
export const labelForSource = (source: string): string =>
  SOURCE_LABELS[source as TraceSource] ?? 'Entry';

/**
 * How a business day in the breakdown is marked, or `null` for a date with no
 * state to mark.
 *
 * An unrecognised value answers `null` too, on the same principle: this app's
 * only honest options are the two the document publishes, and reading a third as
 * `Open` would say a settled figure is provisional or the reverse. The date and
 * its count are still shown either way, which is the answer the reader came for.
 */
export function describeDayState(
  state: string | null,
): { readonly label: string; readonly variant: 'secondary' | 'outline' } | null {
  if (state === 'CLOSED') return { label: 'Closed', variant: 'secondary' };

  if (state === 'OPEN') return { label: 'Open', variant: 'outline' };

  return null;
}

/** One timeline row, in the words and shapes the table renders. */
export interface ReadableEntry {
  /** The kind of record — `'Ledger entry'`. */
  readonly kind: string;

  /** What the row is, in a phrase: the reason, the action, the reference type. */
  readonly what: string;

  /** The trading day it fell in, or `null` on an audit entry, which belongs to none. */
  readonly businessDate: string | null;

  /** Signed on a stock movement, unsigned on a basket line, `null` where nothing is counted. */
  readonly quantity: string | null;

  /** The money, as a magnitude. `null` where the source moves none. */
  readonly amount: string | null;

  /** `'In'` or `'Out'` on a ledger entry, `null` everywhere else. */
  readonly direction: string | null;

  /** Whether this entry compensates another. Both halves appear; neither is netted away. */
  readonly reverses: boolean;
}

/**
 * The eight reasons a stock movement carries, as the generated model publishes
 * them.
 *
 * Needed because the trace types `reason` as a bare `string | null` — the field
 * does double duty across two sources, carrying the movement's enum on one and
 * the free text somebody typed on a refund on the other. `labelForReason` takes
 * the union, so this is the narrowing that lets the movement log's words be
 * reused here instead of re-typed. A value outside the set falls through to the
 * raw text, which is the right answer for a refund's reason and an honest one
 * for an enum this app has not caught up with.
 */
const MOVEMENT_REASONS = new Set<string>([
  'SALE',
  'REFUND_RETURN',
  'DELIVERY',
  'BREAKAGE',
  'SPOILAGE',
  'LOSS',
  'FOUND',
  'STOCKTAKE_VARIANCE',
] satisfies MovementReason[]);

/**
 * What a row says it is.
 *
 * Each source has exactly one field that carries its meaning, and the document
 * says which: `reason` on a stock movement and a refund, `referenceType` on a
 * ledger entry, `action` on an audit entry. A sale and a basket line carry none
 * of the three and are named by their kind alone, which is all there is to say
 * about them.
 */
function whatHappened(entry: TraceEntryResource): string {
  if (entry.source === 'audit_entries') {
    const action = entry.action ?? 'recorded';

    return entry.entityType === null ? action : `${action} — on a ${entry.entityType}`;
  }

  if (entry.source === 'ledger_entries' && entry.referenceType !== null) {
    return labelForReferenceType(entry.referenceType);
  }

  if (entry.reason !== null && entry.reason !== '') {
    // A movement's reason is an enum and gets the movement log's own words —
    // `Sold`, `Counted` — rather than the wire constant. A refund's reason is
    // free text somebody typed and is shown as they typed it.
    return entry.source === 'stock_movements' && MOVEMENT_REASONS.has(entry.reason)
      ? labelForReason(entry.reason as MovementReason)
      : entry.reason;
  }

  return labelForSource(entry.source);
}

export function describeTraceEntry(entry: TraceEntryResource): ReadableEntry {
  return {
    kind: labelForSource(entry.source),
    what: whatHappened(entry),
    businessDate: entry.businessDate,
    // Signed only where the sign is the fact. A basket line's quantity is
    // positive by construction and a `+` on it would suggest a direction it does
    // not have.
    quantity:
      entry.quantity === null
        ? null
        : entry.source === 'stock_movements'
          ? formatMovementQuantity(entry.quantity)
          : String(entry.quantity),
    amount: entry.amountPaise === null ? null : formatPaise(entry.amountPaise),
    direction: entry.direction === 'CREDIT' ? 'In' : entry.direction === 'DEBIT' ? 'Out' : null,
    reverses: entry.reversesEntryId !== null,
  };
}
